import { resolveImageGenerationFormOptions } from './capabilities.js'
import { compileImageTechnicalParams } from './technicalParams.js'
import {
  createImageGenerationIntent,
  createImageGenerationIntentFromPayload
} from './intent.js'
import {
  appendSellerPromptExtensions,
  resolveImageCreatorPrompt,
  resolveImageDirectorPrompt
} from './prompts.js'

function text(value) {
  return typeof value === 'string' ? value.trim() : ''
}

function canonicalValue(value) {
  if (Array.isArray(value)) return value.map(canonicalValue)
  if (value && typeof value === 'object') {
    return Object.keys(value).sort().reduce((result, key) => {
      result[key] = canonicalValue(value[key])
      return result
    }, {})
  }
  return value
}

function valuesEqual(left, right) {
  return JSON.stringify(canonicalValue(left)) === JSON.stringify(canonicalValue(right))
}

function imageCreatorPayload(payload) {
  return payload &&
    payload.media_type === 'image' &&
    Array.isArray(payload.stages) &&
    payload.stages.includes('creator')
}

function assertIntentParity(payload, expected) {
  const actual = payload.image_intent
  if (!actual || typeof actual !== 'object' || Array.isArray(actual)) {
    throw new TypeError('image creator payload requires image_intent')
  }
  const fields = [
    'contract_version',
    'mode',
    'app_code',
    'media_type',
    'prompt',
    'prompt_extensions',
    'reference_asset_ids',
    'technical_params',
    'metadata'
  ]
  const mismatch = fields.find((field) => !valuesEqual(actual[field], expected[field]))
  if (mismatch) {
    throw new TypeError(`image_intent.${mismatch} conflicts with creator payload`)
  }
}

/**
 * The single frontend entry point for image generation behavior. Pages own
 * their UI and business metadata, then reduce that state through this kernel.
 */
export class ImageGenerationKernel {
  resolveFormOptions(family, rights = {}, preferred = {}, options = {}) {
    return resolveImageGenerationFormOptions(family, rights, preferred, options)
  }

  compileTechnicalParams(input = {}) {
    return compileImageTechnicalParams(input)
  }

  resolveCreatorPrompt(input = {}) {
    return resolveImageCreatorPrompt(input)
  }

  resolveDirectorPrompt(prompt = '') {
    return resolveImageDirectorPrompt(prompt)
  }

  appendSellerPromptExtensions(basePrompt = '', extensions = {}) {
    return appendSellerPromptExtensions(basePrompt, extensions)
  }

  createCreatorPayload({
    appCode,
    director = null,
    extraPayload = {},
    mediaType = 'image',
    metadata = {},
    mode,
    originalPrompt,
    outputCount,
    prompt,
    promptExtensions = [],
    referenceAssetIds = [],
    selectedModelKey = '',
    stages = ['creator'],
    technicalParams = {}
  } = {}) {
    const sourcePrompt = typeof originalPrompt === 'undefined' ? prompt : originalPrompt
    const promptContract = this.resolveCreatorPrompt({ director, originalPrompt: sourcePrompt })
    const references = Array.isArray(referenceAssetIds) ? referenceAssetIds.slice() : referenceAssetIds
    const normalizedAppCode = text(appCode)
    const normalizedMediaType = text(mediaType).toLowerCase() || 'image'
    const normalizedStages = Array.isArray(stages) && stages.length
      ? stages.map((stage) => text(stage).toLowerCase()).filter(Boolean)
      : ['creator']
    if (!normalizedStages.includes('creator')) {
      throw new TypeError('image creator payload stages must include creator')
    }
    const resolvedMode = text(mode).toLowerCase() || (
      normalizedAppCode.toLowerCase() === 'ai_edit'
        ? 'edit'
        : (Array.isArray(references) && references.length ? 'reference' : 'generate')
    )
    const intent = createImageGenerationIntent({
      appCode: normalizedAppCode,
      mediaType: normalizedMediaType,
      metadata,
      mode: resolvedMode,
      prompt: promptContract.submitPrompt,
      promptExtensions,
      referenceAssetIds: references,
      technicalParams
    })
    const payload = {
      ...(extraPayload && typeof extraPayload === 'object' ? extraPayload : {}),
      app_code: intent.app_code,
      media_type: intent.media_type,
      stages: normalizedStages,
      prompt: intent.prompt,
      selected_model_key: text(selectedModelKey),
      technical_params: intent.technical_params,
      reference_asset_ids: intent.reference_asset_ids,
      metadata: intent.metadata,
      image_intent: intent
    }
    if (Number.isInteger(outputCount) && outputCount > 0) payload.output_count = outputCount
    return payload
  }

  attachCreatorIntent(payload, options = {}) {
    if (!imageCreatorPayload(payload)) return payload
    if (typeof payload.image_intent !== 'undefined') {
      const expected = createImageGenerationIntentFromPayload(payload, {
        ...options,
        mode: options.mode || payload.image_intent?.mode,
        promptExtensions: options.promptExtensions || payload.image_intent?.prompt_extensions
      })
      assertIntentParity(payload, expected)
      return payload
    }
    const expected = createImageGenerationIntentFromPayload(payload, options)
    return { ...payload, image_intent: expected }
  }

  assertCreatorPayload(payload, options = {}) {
    if (!imageCreatorPayload(payload)) {
      throw new TypeError('payload is not an image creator request')
    }
    const expected = createImageGenerationIntentFromPayload(payload, {
      ...options,
      mode: options.mode || payload.image_intent?.mode,
      promptExtensions: options.promptExtensions || payload.image_intent?.prompt_extensions
    })
    assertIntentParity(payload, expected)
    return payload
  }
}

export const imageGenerationKernel = Object.freeze(new ImageGenerationKernel())

export function createImageCreatorPayload(input = {}) {
  return imageGenerationKernel.createCreatorPayload(input)
}

export function withImageGenerationIntent(payload, options = {}) {
  return imageGenerationKernel.attachCreatorIntent(payload, options)
}

export function assertImageGenerationCreatorPayload(payload, options = {}) {
  return imageGenerationKernel.assertCreatorPayload(payload, options)
}
